import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../redux/store";
import { Button } from "../../../components/button/Button";
import { ModalContext } from "../../../components/Modal";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import {
  saveQuery,
  saveQueryIds,
  setSavedQueryName,
} from "../../../redux/saveSlice";
import { SavedQueryGroup } from "../../../interfaces/queryBuilderInterfaces";
import { showNotification } from "../../../redux/notificationSlice";
import { fetchFolders, saveFolder } from "../../../redux/folderSlice";
import {
  setIsQueryUpdated,
  setIsUpdatedTab,
  tabsData,
} from "../../../redux/queryBuilderSlice";
import { getReportOptions } from "../../../redux/subscribeSlice";
import LoadingOverlay from "../../../components/LoadingOverlay";
import "../../../components/dropdown/multiSelectDropdown.scss";

const SavePopup = ({}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isSaving, setIsSaving] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [description, setDescription] = useState("");
  const modalContext = useContext(ModalContext);
  const close = modalContext ? modalContext.close : () => {};
  const [selectedFolder, setSelectedFolder] = useState<{
    value: string;
    label: string;
  }>({ value: "-1", label: "Default" });
  const currentTabIndex = useSelector(
    (state: any) => state.queryBuilder.currentTabIndex,
  );
  const [folders, setFolders] = useState<{ label: string; value: string }[]>(
    [],
  );
  const [nameError, setNameError] = useState<string | null>(null);
  const [tabSelectionError, setTabSelectionError] = useState<string | null>(
    null,
  );
  const queryIds = useSelector((state: any) => state.save.queryIds);
  const tabs = useSelector(tabsData) ?? [];
  const [selectedTabIndexes, setSelectedTabIndexes] = useState<number[]>([]);
  useEffect(() => {
    dispatch(fetchFolders());
  }, [dispatch]);

  const foldersList = useSelector((state: RootState) => state.folder.folders);
  const title =
    useSelector((state: RootState) => state.save.savePopUpTitle) ?? "Save New";

  useEffect(() => {
    if ((foldersList[0]?.children ?? []).length > 0) {
      const defaultFolder = foldersList[0].children?.find(
        (folder: any) => folder.name === "Default",
      );
      const folders =
        foldersList[0].children?.map((folder: any) => ({
          label: folder.name,
          value: folder.id,
        })) || [];
      setFolders(folders);
      if (defaultFolder) {
        setSelectedFolder({
          value: defaultFolder.id,
          label: defaultFolder.name,
        });
      }
    }
  }, [foldersList]);

  useEffect(() => {
    setSelectedTabIndexes(tabs.map((_: any, index: number) => index));
  }, []);

  const handleFolderChange = (newValue: string) => {
    const selected = folders.find(
      (folder) => Number(folder.value) === Number(newValue),
    );
    if (selected) {
      setSelectedFolder({ value: selected.value, label: selected.label });
    }
  };

  const selectedTabs = useMemo(
    () =>
      selectedTabIndexes
        .map((index) => ({ index, tab: tabs[index] }))
        .filter((item) => item.tab),
    [selectedTabIndexes, tabs],
  );

  const allTabsSelected =
    tabs.length > 0 && selectedTabIndexes.length === tabs.length;

  const handleTabSelectionChange = (tabIndex: number) => {
    setSelectedTabIndexes((prevSelected) => {
      const isSelected = prevSelected.includes(tabIndex);
      return isSelected
        ? prevSelected.filter((index) => index !== tabIndex)
        : [...prevSelected, tabIndex].sort((left, right) => left - right);
    });

    if (tabSelectionError) {
      setTabSelectionError(null);
    }
  };

  const handleSelectAllTabs = () => {
    setSelectedTabIndexes(
      allTabsSelected ? [] : tabs.map((_: any, index: number) => index),
    );

    if (tabSelectionError) {
      setTabSelectionError(null);
    }
  };

  const selectAllTabs = () => {
    setSelectedTabIndexes(tabs.map((_: any, index: number) => index));

    if (tabSelectionError) {
      setTabSelectionError(null);
    }
  };

  const deselectAllTabs = () => {
    setSelectedTabIndexes([]);
  };

  function handleSaveFolder() {
    const saveQueryResult = async () => {
      if (selectedTabs.length > 0) {
        const name = saveName.trim();
        if (!name) {
          setNameError("Name is required");
          return;
        }

        if (selectedTabs.length === 0) {
          setTabSelectionError("Select at least one tab to save");
          return;
        }

        setIsSaving(true);

        const payload: SavedQueryGroup = {
          name,
          description,
          savedQueryList:
            selectedTabs.length > 0
              ? selectedTabs.map(({ tab, index }) => {
                  return {
                    olapQueryName: tab.queryName,
                    name: tab.tabName,
                    description: "",
                    queryId: (queryIds && queryIds[index]) || null,
                    isShared: false,
                    isFlattenedView: false,
                    type: tab.queryMode,
                    subType: tab.querySubType,
                    gridSorts: tab.gridSorts,
                    ...(tab.queryMode === "Map" && {
                      mapOptions: { measureSelections: tab.msrSelections },
                    }),
                  };
                })
              : [],
        };
        let result = await dispatch(saveQuery(payload));

        if (result.type === "savegroup/save/fulfilled") {
          if (result.payload === -1) {
            setIsSaving(false);
            setNameError("Query name already exist");
            return;
          } else {
            const queryData: any = await dispatch(
              getReportOptions(result.payload),
            );
            if (
              queryData.type === "reportOptions/fulfilled" &&
              queryData.payload &&
              queryData.payload.length > 0
            ) {
              const updatedQueryIds = [...queryIds];

              selectedTabs.forEach(({ index }, selectedIndex) => {
                updatedQueryIds[index] =
                  queryData.payload[selectedIndex]?.key ??
                  updatedQueryIds[index] ??
                  null;
              });

              dispatch(saveQueryIds(updatedQueryIds));
            }
            if (Number(selectedFolder.value) >= 0) {
              await dispatch(
                saveFolder({
                  folderId: selectedFolder.value,
                  reportId: result.payload as string,
                }),
              );
            }
          }

          selectedTabs.forEach(({ index }) => {
            dispatch(setSavedQueryName({ name: payload.name, index }));
            dispatch(setIsUpdatedTab({ flag: false, currentTabIndex: index }));
            dispatch(
              setIsQueryUpdated({ flag: false, currentTabIndex: index }),
            );
          });

          setIsSaving(false);

          dispatch(
            showNotification({
              message: "Query saved successfully",
              duration: 3000,
            }),
          );
        } else {
          setIsSaving(false);

          dispatch(
            showNotification({
              message: "Error in saving query",
              duration: 3000,
              isError: true,
            }),
          );
        }
        close();
      }
    };
    saveQueryResult();
  }

  return (
    <div className="dialog-container dialog-box-min filter-box">
      <LoadingOverlay show={isSaving} />
      <div className="dialog-header"></div>
      <div className="dialog-title">
        <h3>{title}</h3>
      </div>
      <div className="dialog-body">
        <div className="input-group">
          <label htmlFor="save-name">Name</label>
          <input
            type="text"
            id="save-name"
            className="input-field"
            maxLength={50}
            value={saveName}
            onChange={(event) => {
              setSaveName(event.target.value);
              if (nameError) {
                setNameError(null);
              }
            }}
          />
          {nameError && (
            <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>
              {nameError}
            </div>
          )}
        </div>
        <div className="input-group">
          <label htmlFor="save-description">Description</label>
          <input
            type="text"
            id="save-description"
            className="input-field"
            maxLength={100}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
        <Dropdown
          label="Folder"
          id="save-folder"
          selectedValue={selectedFolder.value}
          options={folders}
          onChange={(newValue) => handleFolderChange(newValue)}
        />
        <div className="input-group">
          <label style={{ marginBottom: "8px" }}>Choose Tabs</label>
          <div className="filter-options-section" style={{}}>
            <div className="filter-checkbox-section">
              <label
                className="radio-inline"
                htmlFor="save-all-tabs"
                style={{ alignItems: "center" }}
              >
                <input
                  id="save-all-tabs"
                  type="checkbox"
                  checked={allTabsSelected}
                  onChange={handleSelectAllTabs}
                  style={{ display: "none" }}
                />
                <span
                  className="msd-checkbox"
                  style={{ width: 13, height: 13 }}
                  aria-hidden="true"
                  data-state={allTabsSelected ? "checked" : "unchecked"}
                />
                <span className="ellipsis">Select All</span>
              </label>
            </div>
            <div className="filter-button-section">
              <Button
                color="secondary"
                className="btn md"
                variant="plain"
                id="btn-deselect-all-tabs"
                onClick={deselectAllTabs}
                disabled={selectedTabIndexes.length === 0}
              >
                Deselect All
              </Button>
            </div>
          </div>
          <div
            style={{
              maxHeight: "180px",
              overflowY: "auto",
              border: "1px solid #d7dbe0",
              borderRadius: "4px",
              padding: "8px 12px",
            }}
          >
            {tabs.map((tab: any, index: number) => {
              const isSelected = selectedTabIndexes.includes(index);
              const isCurrentTab = index === currentTabIndex;

              return (
                <label
                  key={`${tab.tabName}-${index}`}
                  className="radio-inline"
                  htmlFor={`save-tab-${index}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "4px 0",
                    fontWeight: "normal",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      minWidth: 0,
                    }}
                  >
                    <input
                      id={`save-tab-${index}`}
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleTabSelectionChange(index)}
                      style={{ display: "none" }}
                    />
                    <span
                      className="msd-checkbox"
                      aria-hidden="true"
                      data-state={isSelected ? "checked" : "unchecked"}
                    />
                    <span
                      className="ellipsis"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {tab.tabName}
                    </span>
                  </span>
                  {isCurrentTab && (
                    <span
                      style={{ fontSize: "12px", color: "#666", flexShrink: 0 }}
                    >
                      Current
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          {tabSelectionError && (
            <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>
              {tabSelectionError}
            </div>
          )}
        </div>
      </div>

      <div className="dialog-footer">
        <div className="apply-cancel">
          <Button
            color="primary"
            className="btn md"
            variant="solid"
            id="btn-apply"
            onClick={handleSaveFolder}
            disabled={isSaving || selectedTabIndexes.length === 0}
          >
            Apply
          </Button>
          <Button
            color="primary"
            className="btn md"
            variant="outline"
            id="btn-close"
            onClick={close}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SavePopup;
